import React, { useState, useMemo } from "react";
import { Slider } from "./ui/slider";
import { Calculator, TrendUp } from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useRate } from "../context/RateContext";
import { monthlyPayment, totalCredit, formatMAD, formatEUR } from "../lib/format";

const DURATIONS = [10, 20, 25];

const MonthlySimulator = ({ embedded = false, initialPrice = 1850000 }) => {
  const { t, lang } = useT();
  const rate = useRate();

  const [price, setPrice] = useState(initialPrice);
  const [contribPct, setContribPct] = useState(20);
  const [years, setYears] = useState(20);
  const [interest, setInterest] = useState(rate.interest_rate || 4.5);

  const contribution = (price * contribPct) / 100;
  const loan = Math.max(price - contribution, 0);
  const monthly = useMemo(() => monthlyPayment(loan, interest, years), [loan, interest, years]);
  const cost = useMemo(() => totalCredit(loan, interest, years), [loan, interest, years]);

  const toEur = (v) => v * rate.mad_to_eur;

  const Panel = (
    <div className={`grid lg:grid-cols-2 gap-10 ${embedded ? "" : "items-start"}`}>
      {/* Controls */}
      <div className="space-y-8">
        <div>
          <div className="flex justify-between items-baseline mb-3">
            <label className="text-sm font-medium text-white/80">{t("sim.price")}</label>
            <span className="font-mono text-gold">{formatMAD(price, lang)} {t("common.mad")}</span>
          </div>
          <Slider data-testid="sim-price-slider" value={[price]} min={300000} max={12000000} step={50000} onValueChange={(v) => setPrice(v[0])} />
          <div className="text-xs text-white/50 mt-1">≈ {formatEUR(toEur(price), lang)} €</div>
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <label className="text-sm font-medium text-white/80">{t("sim.contribution")}</label>
            <span className="font-mono text-gold">{contribPct}% · {formatMAD(contribution, lang)} {t("common.mad")}</span>
          </div>
          <Slider data-testid="sim-contribution-slider" value={[contribPct]} min={0} max={80} step={5} onValueChange={(v) => setContribPct(v[0])} />
          <div data-testid="sim-contribution-eur" className="text-xs text-white/50 mt-1">≈ {formatEUR(toEur(contribution), lang)} €</div>
        </div>

        <div>
          <label className="text-sm font-medium text-white/80 block mb-3">{t("sim.duration")}</label>
          <div className="grid grid-cols-3 gap-3">
            {DURATIONS.map((d) => (
              <button
                key={d}
                data-testid={`sim-duration-${d}`}
                onClick={() => setYears(d)}
                className={`py-3 rounded-full text-sm font-semibold border transition-colors ${
                  years === d ? "bg-gold text-navy border-gold" : "border-white/20 text-white/70 hover:border-gold/60"
                }`}
              >
                {d} {t("sim.years")}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <label className="text-sm font-medium text-white/80">{t("sim.rate")}</label>
            <span className="font-mono text-gold">{interest.toFixed(1)} %</span>
          </div>
          <Slider data-testid="sim-rate-slider" value={[interest]} min={2} max={8} step={0.1} onValueChange={(v) => setInterest(v[0])} />
        </div>
      </div>

      {/* Result */}
      <div className="bg-white/[0.04] border border-white/10 p-8 backdrop-blur-sm">
        <div className="text-center pb-6 border-b border-white/10">
          <div className="overline text-gold mb-3">{t("sim.monthly")}</div>
          <div data-testid="sim-monthly-mad" className="font-display text-4xl sm:text-5xl text-white">
            {formatMAD(monthly, lang)} <span className="text-2xl text-white/60">{t("common.mad")}</span>
          </div>
          <div data-testid="sim-monthly-eur" className="text-gold font-semibold text-lg mt-1">≈ {formatEUR(toEur(monthly), lang)} € {t("prop.per_month")}</div>
        </div>

        <div className="space-y-4 pt-6">
          <Row label={t("sim.contribution")} mad={contribution} eur={toEur(contribution)} lang={lang} t={t} />
          <Row label={t("sim.loan_amount")} mad={loan} eur={toEur(loan)} lang={lang} t={t} />
          <Row label={t("sim.total_cost")} mad={cost} eur={toEur(cost)} lang={lang} t={t} />
        </div>

        <div className="mt-6 flex items-center gap-2 text-xs text-white/40">
          <TrendUp size={16} weight="light" className="text-gold" />
          {t("sim.rate_live")}: 1 {t("common.eur")} ≈ {rate.eur_to_mad?.toFixed?.(2)} {t("common.mad")}
        </div>
        <p className="mt-3 text-xs text-white/40 leading-relaxed">{t("sim.disclaimer")}</p>
      </div>
    </div>
  );

  if (embedded) return Panel;

  return (
    <section id="simulator" data-testid="simulator-section" className="bg-[#0B111A] relative z-10 py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-4">
          <Calculator size={28} weight="light" className="text-gold" />
          <span className="overline text-gold">{t("sim.overline")}</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight mb-4 max-w-2xl">{t("sim.title")}</h2>
        <p className="text-white/60 max-w-2xl mb-12">{t("sim.subtitle")}</p>
        {Panel}
      </div>
    </section>
  );
};

const Row = ({ label, mad, eur, lang, t }) => (
  <div className="flex justify-between items-baseline">
    <span className="text-sm text-white/60">{label}</span>
    <div className="text-end">
      <div className="font-mono text-white">{formatMAD(mad, lang)} {t("common.mad")}</div>
      <div className="text-xs text-gold">≈ {formatEUR(eur, lang)} €</div>
    </div>
  </div>
);

export default MonthlySimulator;
